import { useRef, useEffect, RefObject, useCallback, useLayoutEffect, useState } from 'react';
import classnames from 'classnames';

import { ScrollGestureEvent, initScrollHandlers, clamp } from '@hh.ru/magritte-internal-custom-scroll';
import { ScrollInternalState, ItemWithOffset, OFFSET_ATTR } from '@hh.ru/magritte-ui-scrollable-container/types';
import { debounce } from '@hh.ru/magritte-ui-scrollable-container/utils';

import styles from './scrollableContainer.less';

const BUTTON_WIDTH = 44;
const FOCUS_OUTLINE_WIDTH = 4;
const DEFAULT_DOT = 1;

/**
 * На базе стейта проверяем возможность использования вычисленного index-а
 * @param index значение для проверки
 * @param state Состояние скролла
 */
const isIncorrectIndex = (index: number, state: ScrollInternalState) => index < 0 || state.items.length - 1 < index;

/**
 * Валидирует значение анимации и устанавливает ее в контейнер
 * @param translation значение анимации
 * @param state Состояние скролла
 */
const setTranslation = (translation: number, state: ScrollInternalState) => {
    const { positionContainerRef, positionContainerWidth, containerWidth } = state;
    if (!positionContainerRef.current) {
        return;
    }
    const prevTranslation = state.positionContainerTranslation;
    state.positionContainerTranslation = clamp(translation, containerWidth - positionContainerWidth, 0);
    positionContainerRef.current.style.transform = `translate3d(${state.positionContainerTranslation}px, 0, 0)`;
    if (prevTranslation !== state.positionContainerTranslation) {
        state.onScroll?.();
    }
};

/**
 * Вычисляет нужно ли показывать фейды по краям и скрывать кнопки скролла
 * Применяет соответствующие классы к обертке
 * Проверяет что сдвиг контейнера позиционирования находится в допустимых пределах и если нет, то корректирует его
 * @param state Состояние скролла
 */
const applyScrollButtonsUpdate = (state: ScrollInternalState) => {
    const { positionContainerRef, wrapperRef, positionContainerWidth, containerWidth } = state;
    let { positionContainerTranslation } = state;

    if (!positionContainerRef.current || !wrapperRef.current) {
        return;
    }

    if (positionContainerWidth + positionContainerTranslation < containerWidth) {
        positionContainerTranslation = containerWidth - positionContainerWidth;
        setTranslation(positionContainerTranslation, state);
    }
    const isFadeLeft = positionContainerTranslation < 0;
    const isFadeRight = positionContainerTranslation + positionContainerWidth > containerWidth;
    const className = classnames({
        [styles.fadeLeftSide]: isFadeLeft && !isFadeRight,
        [styles.fadeRightSide]: isFadeRight && !isFadeLeft,
        [styles.fadeBothSides]: isFadeRight && isFadeLeft,
    });
    wrapperRef.current.classList.remove(styles.fadeBothSides, styles.fadeLeftSide, styles.fadeRightSide);
    className && wrapperRef.current.classList.add(className);
};

const setAnimated = (state: ScrollInternalState, animated: boolean) => {
    state.positionContainerRef.current?.classList.toggle(styles.positionContainerAnimated, animated);
    if (!animated) {
        state.scrollAnimationInProgress = false;
    }
};

/**
 * Скролл к конкретному элементу группы. Если на момент вызова нет ref контейнера позиционирования, то считаем что
 * функция была при маунте компонента и запоминаем элемент на который нужно скроллить. При маунте вызываем эту функцию
 * еще раз и скроллим к запомненному элементу без анимации.
 * @param state Состояние скролла
 * @param item Элемент к которому нужно подскроллить
 */
const scrollTo = (state: ScrollInternalState, item: RefObject<HTMLElement>) => {
    const {
        positionContainerTranslation,
        containerWidth,
        positionContainerRef,
        scrollToItemInProgress,
        disableButtons,
        positionContainerWidth,
        showDotPages,
        items,
    } = state;
    if (!positionContainerRef.current) {
        state.scrollToOnMount = item;
        return;
    }

    if (item.current === scrollToItemInProgress?.current) {
        return;
    }
    state.previousActiveIndexItem = items.indexOf(item as unknown as RefObject<HTMLElement & ItemWithOffset>);
    const offset = (item.current as unknown as ItemWithOffset)[OFFSET_ATTR];
    const width = offset.right - offset.left;
    const buttonsCorrection = disableButtons ? 0 : BUTTON_WIDTH;

    // нужно ли отцентрировать контейнер по текущему item
    // актуально для режима showDotPages
    // так как контейнер работает как карусель
    const needItemCorrection = showDotPages;
    let translation = positionContainerTranslation;
    if (width > containerWidth) {
        // элемент шире контейнера
        translation = -((width - containerWidth) / 2 + offset.left);
    } else if (offset.left + positionContainerTranslation < buttonsCorrection && positionContainerTranslation < 0) {
        // контейнер движется вправо
        const correction = needItemCorrection ? (containerWidth - width) / 2 : buttonsCorrection;
        translation = -offset.left + correction;
    } else if (
        offset.right + positionContainerTranslation > containerWidth - buttonsCorrection &&
        positionContainerTranslation > containerWidth - positionContainerWidth
    ) {
        // контейнер движется влево
        const correction = needItemCorrection ? (containerWidth - width) / 2 : buttonsCorrection;
        translation = containerWidth - offset.right - correction;
    } else if (showDotPages) {
        // анимация к центру элемента (при необходимости)
        const correction = (containerWidth - width) / 2;
        // если находимся на границе контейнера, то анимации уже не будет (так как граница блока достигнута)
        // то есть translation надо скорректировать на базе контейнера чрз clamp
        translation = clamp(-offset.left + correction, containerWidth - positionContainerWidth, 0);
    }

    const isPositionChanged = translation !== positionContainerTranslation;
    const needAnimation = !state.scrollToOnMount && isPositionChanged;

    // Если это скролл при маунте, или позиция скролла не изменилась, то его не нужно анимировать
    setAnimated(state, needAnimation);
    setTranslation(translation, state);
    if (needAnimation) {
        state.scrollAnimationInProgress = true;
        state.scrollToItemInProgress = item;
    }
    applyScrollButtonsUpdate(state);
};

/**
 * Если текущий элемент совпадает с новым
 * Находим следующий активный элемент по направлению и скролим к нему
 */
const scrollToNewItemDebounced = debounce((state: ScrollInternalState, indexNearestToCenter: number) => {
    const { previousActiveIndexItem, items, directionRight } = state;
    let index = indexNearestToCenter;
    // определяем ситуацию, когда скрола не хватило до следующего элемента
    if (previousActiveIndexItem === indexNearestToCenter) {
        const indexBasedOnDirection = directionRight ? indexNearestToCenter + 1 : indexNearestToCenter - 1;
        // нужно проверить новый индекс, чтобы не выходить за границы
        if (!isIncorrectIndex(indexBasedOnDirection, state)) {
            index = indexBasedOnDirection;
            state.setActiveDot(index + 1);
        }
    }
    const item: RefObject<HTMLElement> = items[index];
    scrollTo(state, item);
}, 30);

/**
 * Скролл к конкретному элементу группы (обернуто в debounce)
 */
const scrollToDebounced = debounce(
    (state: ScrollInternalState, index: number) => scrollTo(state, state.items[index]),
    30
);

/**
 * Пересчитывает позиции элементов и обновляет состояние скролла. Вызывается при изменении размеров контейнера или
 * добавлении/удалении элементов. Проходится по всем элементам и вычисляет сдвиг их правой и левой границы относительно
 * контейнера позиционирования. Сортирует элементы по возрастанию сдвига левой границы. Вычисляет ширину внешнего
 * контейнера и контейнера позиционирования. При вычислении ширины контейнера позиционирования правой его границей
 * считается правая граница самого крайнего справа элемента. Это нужно чтобы при переносе элементов на несколько строк
 * справа не появлялось лишнее пустое пространство.
 * @param state Состояние скролла
 */
const recalculateItemPositions = (state: ScrollInternalState) => {
    const { positionContainerRef, containerRef, items, showDotPages, setCountItems } = state;

    if (!positionContainerRef.current || !containerRef.current) {
        return;
    }
    const offset = positionContainerRef.current.getBoundingClientRect().left || 0;
    items.forEach((itemRef) => {
        if (!itemRef.current) {
            return;
        }
        const rect = itemRef.current.getBoundingClientRect();
        itemRef.current[OFFSET_ATTR] = { left: rect.left - offset, right: rect.right - offset };
    });

    if (showDotPages) {
        setCountItems(items.length);
    }

    state.recalcRequired = false;
    // т.к. нам нужна ширина content-box, то вычитаем левую и правую коррекцию, т.к. ей будут равны паддинги
    state.containerWidth =
        containerRef.current.offsetWidth - state.correction.leftCorrection - state.correction.rightCorrection;
    // считаем ширину контейнера позиционирования равной правой границе самого крайнего элемента
    state.positionContainerWidth = items.reduce(
        (position, itemRef) => Math.max(position, itemRef.current?.[OFFSET_ATTR]?.right || 0),
        0
    );
    if (!state.disableButtons && state.positionContainerWidth > state.containerWidth) {
        state.containerWidth -= FOCUS_OUTLINE_WIDTH;
    }

    // если при маунте, когда у нас еще не было информации об элементах и размерах контейнеров был вызван scrollTo,
    // то нужно проскроллить без анимации
    if (state.scrollToOnMount) {
        scrollTo(state, state.scrollToOnMount);
        state.scrollToOnMount = null;
        return;
    }

    if (state.showDotPages && state.activeDot && items[state.activeDot - 1] && items[state.activeDot - 1].current) {
        const item = items[state.activeDot - 1].current as HTMLElement & ItemWithOffset;
        const offset = item[OFFSET_ATTR];
        const itemWidth = offset.right - offset.left;
        const translation = (state.containerWidth - itemWidth) / 2 - offset.left;

        setAnimated(state, false);
        setTranslation(translation, state);
    }

    applyScrollButtonsUpdate(state);
};

/**
 * Инициализирует ResizeObserver для контейнера и контейнера позиционирования
 */
const initResizeObserver = (state: ScrollInternalState) => {
    const { positionContainerRef, containerRef } = state;
    if (!positionContainerRef.current || !containerRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {};
    }
    const observer = new ResizeObserver(() => {
        recalculateItemPositions(state);
    });
    observer.observe(containerRef.current);
    observer.observe(positionContainerRef.current);

    return () => observer.disconnect();
};

/**
 * На базе стейта считает ближайший элемент, который ближе к центру вью контейнера
 * Между блоками могут быть отступы, тогда centerPoint в offset-ы ни одного из элементов не попадет
 * В этом случае, используем предыдущее значение
 * @param state Состояние скролла
 * @returns Индекс элемента в массиве state.items
 */
const findIndexNearestToCenter = (state: ScrollInternalState): number => {
    const centerPoint = state.containerWidth / 2 + Math.abs(state.positionContainerTranslation);
    const indexNearestToCenter = state.items.findIndex((itemRef) => {
        if (!itemRef.current) {
            return false;
        }
        const offset = itemRef.current[OFFSET_ATTR];
        return offset.left < centerPoint && offset.right > centerPoint;
    });
    return indexNearestToCenter !== -1 ? indexNearestToCenter + 1 : state.activeDot;
};

interface UseScrollContainer {
    registerItem: (ref: RefObject<HTMLElement>) => void;
    removeItem: (ref: RefObject<HTMLElement>) => void;
    scrollToItem: (ref: RefObject<HTMLElement>) => void;
    scrollToFirstItem: VoidFunction;
    scrollToLastItem: VoidFunction;
    scrollSide: (right?: boolean) => void;
    initScrollHandlers: () => VoidFunction;
    wrapperRef: RefObject<HTMLDivElement>;
    positionContainerRef: RefObject<HTMLDivElement>;
    containerRef: RefObject<HTMLDivElement>;
    countItems: number;
    activeDot: number;
    isScrollRightEnabled: () => boolean;
    isScrollLeftEnabled: () => boolean;
}

export const useScrollContainer = (
    leftCorrection: number,
    rightCorrection: number,
    disableButtons: boolean,
    showDotPages?: boolean,
    onScroll?: () => void
): UseScrollContainer => {
    // need state for dot component
    const [activeDot, setActiveDot] = useState(DEFAULT_DOT);
    const [countItems, setCountItems] = useState(0);
    const scrollStateRef = useRef<ScrollInternalState>({
        wrapperRef: useRef<HTMLDivElement>(null),
        positionContainerRef: useRef<HTMLDivElement>(null),
        containerRef: useRef<HTMLDivElement>(null),
        positionContainerWidth: 0,
        positionContainerTranslation: 0,
        containerWidth: 0,
        items: [],
        correction: { leftCorrection: 0, rightCorrection: 0 },
        disableButtons: false,
        scrollAnimationInProgress: false,
        scrollToOnMount: null,
        recalcRequired: false,
        scrollToItemInProgress: null,
        showDotPages: false,
        activeDot: DEFAULT_DOT,
        needCorrectionOnScrollEnd: false,
        setActiveDot,
        setCountItems,
        directionRight: false,
        previousActiveIndexItem: 0,
        onScroll,
        wasInteracted: false,
    });

    scrollStateRef.current.correction = { leftCorrection, rightCorrection };
    scrollStateRef.current.disableButtons = disableButtons;
    scrollStateRef.current.showDotPages = Boolean(showDotPages);
    scrollStateRef.current.activeDot = activeDot;
    scrollStateRef.current.onScroll = onScroll;

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    const recalculateItemPositionsDebounced = useCallback(
        debounce(() => recalculateItemPositions(scrollStateRef.current), 10),
        []
    );

    const scrollToItem = useCallback((ref: RefObject<HTMLElement>) => {
        if (scrollStateRef.current.recalcRequired) {
            recalculateItemPositions(scrollStateRef.current);
        }
        scrollStateRef.current.wasInteracted = true;
        // set active dot here because user can click on item
        if (scrollStateRef.current.showDotPages) {
            scrollStateRef.current.setActiveDot(
                scrollStateRef.current.items.indexOf(ref as unknown as RefObject<HTMLElement & ItemWithOffset>) + 1
            );
        }
        scrollTo(scrollStateRef.current, ref);
    }, []);

    const scrollToFirstItem = useCallback(() => {
        const { items } = scrollStateRef.current;
        items.length > 0 && scrollToItem(items[0]);
    }, [scrollToItem]);

    const scrollToLastItem = useCallback(() => {
        const { items } = scrollStateRef.current;
        items.length > 0 && scrollToItem(items[items.length - 1]);
    }, [scrollToItem]);

    const scrollSide = useCallback(
        (directionRight?: boolean) => {
            const { containerWidth, positionContainerRef, positionContainerTranslation, showDotPages } =
                scrollStateRef.current;
            scrollStateRef.current.wasInteracted = true;
            if (!positionContainerRef.current) {
                return;
            }
            if (!showDotPages) {
                // move container based on container width
                const translation = positionContainerTranslation + (directionRight ? -containerWidth : containerWidth);
                setAnimated(scrollStateRef.current, true);
                setTranslation(translation, scrollStateRef.current);
                applyScrollButtonsUpdate(scrollStateRef.current);
            } else {
                // calc new dot and check it
                const activeDot = scrollStateRef.current.activeDot;
                const newActiveDot = directionRight ? activeDot + 1 : activeDot - 1;
                if (!isIncorrectIndex(newActiveDot - 1, scrollStateRef.current)) {
                    // move container to new select ref
                    scrollToItem(scrollStateRef.current.items[newActiveDot - 1]);
                }
            }
        },
        [scrollToItem]
    );

    const registerItem = useCallback((ref: RefObject<HTMLElement>) => {
        if (!ref.current) {
            return;
        }

        scrollStateRef.current.items.push(ref as unknown as RefObject<HTMLElement & ItemWithOffset>);
        scrollStateRef.current.recalcRequired = true;
        recalculateItemPositions(scrollStateRef.current);
    }, []);

    const removeItem = useCallback(
        (ref: RefObject<HTMLElement>) => {
            if (!ref.current) {
                return;
            }

            scrollStateRef.current.items = scrollStateRef.current.items.filter(
                (storedRef) => storedRef.current !== ref.current
            );
            recalculateItemPositionsDebounced();
        },
        [recalculateItemPositionsDebounced]
    );

    useEffect(() => {
        const { wrapperRef, positionContainerRef, containerRef } = scrollStateRef.current;
        if (!containerRef.current || !positionContainerRef.current || !wrapperRef.current) {
            return void 0;
        }

        const transitionEndHandler = (event: TransitionEvent) => {
            if (event.target !== scrollStateRef.current?.positionContainerRef.current) {
                return;
            }
            setAnimated(scrollStateRef.current, false);
            if (scrollStateRef.current) {
                scrollStateRef.current.scrollAnimationInProgress = false;
                scrollStateRef.current.scrollToItemInProgress = null;
            }
        };
        const positionContainer = scrollStateRef.current?.positionContainerRef.current;
        positionContainer?.addEventListener('transitionend', transitionEndHandler);

        const resizeObserverCleanup = initResizeObserver(scrollStateRef.current);

        return () => {
            resizeObserverCleanup();
            positionContainer?.removeEventListener('transitionend', transitionEndHandler);
        };
    }, []);

    useLayoutEffect(() => {
        recalculateItemPositions(scrollStateRef.current);
        if (disableButtons) {
            setAnimated(scrollStateRef.current, false);
        }
    }, [disableButtons, leftCorrection, rightCorrection]);

    useEffect(() => {
        const wrapperRef = scrollStateRef.current.wrapperRef.current;
        if (!wrapperRef) {
            return void 0;
        }
        const handleTouchStartHandler = (event: TouchEvent) => {
            event.stopPropagation();
        };
        wrapperRef.addEventListener('touchstart', handleTouchStartHandler);
        return () => {
            wrapperRef.removeEventListener('touchstart', handleTouchStartHandler);
        };
    }, []);

    useLayoutEffect(() => {
        /* Эффект нужен для пересчета размеров в случае, когда родительский контейнер открывается с анимацией,
         * на которую не срабатывает ResizeObserver - например, Modal с transition: scale */
        const handleParentTransitionEnd = (event: TransitionEvent) => {
            const { wrapperRef, wasInteracted } = scrollStateRef.current;
            /* wasInteracted - флаг, было ли взаимодействие с компонентом (скролл или подскролл к какому-либо элементу).
             * Если взаимодействие было, то пересчитывать не нужно, тк считаем, что анимации появления родительского контейнера
             * завершились */
            if (
                !wasInteracted &&
                event.target instanceof Element &&
                wrapperRef.current &&
                event.target.contains(wrapperRef.current)
            ) {
                recalculateItemPositions(scrollStateRef.current);
            }
        };

        document.addEventListener('transitionend', handleParentTransitionEnd);

        return () => {
            document.removeEventListener('transitionend', handleParentTransitionEnd);
        };
    }, []);

    const onTouchMove = useCallback((event: ScrollGestureEvent) => {
        if (scrollStateRef.current.scrollAnimationInProgress) {
            return;
        }
        scrollStateRef.current.wasInteracted = true;
        const { positionContainerTranslation } = scrollStateRef.current;
        setTranslation(scrollStateRef.current.positionContainerTranslation + event.delta, scrollStateRef.current);
        if (positionContainerTranslation === scrollStateRef.current.positionContainerTranslation) {
            event.preventDefault();
        }
        if (scrollStateRef.current.showDotPages) {
            // by touch change active dot
            const newActiveDot = findIndexNearestToCenter(scrollStateRef.current);
            // update state
            scrollStateRef.current.setActiveDot(newActiveDot);
            // scheduling correction
            scrollStateRef.current.needCorrectionOnScrollEnd = true;
        }
    }, []);

    const onTouchEnd = useCallback(() => {
        const { needCorrectionOnScrollEnd, showDotPages, activeDot } = scrollStateRef.current;
        if (showDotPages && needCorrectionOnScrollEnd) {
            scrollStateRef.current.needCorrectionOnScrollEnd = false;
            // center active element
            scrollToDebounced(scrollStateRef.current, activeDot - 1);
        }
    }, []);

    const onWheelMove = useCallback((event: ScrollGestureEvent) => {
        const { recalcRequired, scrollAnimationInProgress, positionContainerTranslation, showDotPages } =
            scrollStateRef.current;
        scrollStateRef.current.wasInteracted = true;
        if (recalcRequired) {
            recalculateItemPositions(scrollStateRef.current);
        }
        if (scrollAnimationInProgress) {
            event.preventDefault();
            return;
        }
        scrollStateRef.current.directionRight = event.delta < 0;
        const translation = positionContainerTranslation + event.delta;
        setTranslation(translation, scrollStateRef.current);
        applyScrollButtonsUpdate(scrollStateRef.current);

        // эти значения могут быть не равны только в случае если попытались сдвинуть контейнер дальше
        // допустимого значения, это значит что мы уперлись в левую или правую границу, значит дальнейшие события
        // скролла можно не обрабатывать
        if (translation === scrollStateRef.current.positionContainerTranslation) {
            event.preventDefault();
        }
        if (showDotPages) {
            // by wheel change active dot
            const newActiveDot = findIndexNearestToCenter(scrollStateRef.current);
            scrollStateRef.current.setActiveDot(newActiveDot);
            // center active element
            scrollToNewItemDebounced(scrollStateRef.current, newActiveDot - 1);
        }
    }, []);

    const _initScrollHandlers = useCallback(
        () =>
            initScrollHandlers({
                axis: 'horizontal',
                wrapperRef: scrollStateRef.current.wrapperRef,
                onTouchMove,
                onTouchEnd,
                onWheelMove,
            }),
        [onTouchMove, onTouchEnd, onWheelMove]
    );

    const isScrollRightEnabled = useCallback(() => {
        const { positionContainerTranslation, positionContainerWidth, containerWidth } = scrollStateRef.current;
        return positionContainerTranslation + positionContainerWidth <= containerWidth;
    }, []);

    const isScrollLeftEnabled = useCallback(() => {
        const { positionContainerTranslation } = scrollStateRef.current;
        return positionContainerTranslation >= 0;
    }, []);

    return {
        registerItem,
        removeItem,
        scrollToItem,
        scrollToFirstItem,
        scrollToLastItem,
        scrollSide,
        initScrollHandlers: _initScrollHandlers,
        wrapperRef: scrollStateRef.current.wrapperRef,
        positionContainerRef: scrollStateRef.current.positionContainerRef,
        containerRef: scrollStateRef.current.containerRef,
        countItems,
        activeDot,
        isScrollRightEnabled,
        isScrollLeftEnabled,
    };
};
